import React, { useState, useEffect } from 'react';
import { MapContainer, TileLayer, Marker, Tooltip } from 'react-leaflet';
import L from 'leaflet';
import { venueDetailsMapping } from '../misc/venueDetailsMapping';
import 'leaflet/dist/leaflet.css';
import '../styles/Distance.css';

import markerRed from '../assets/map-marker-red.png';
import markerBlack from '../assets/map-marker-black.png';
import DangerIcon from '../assets/modal-icon-danger.png';

const pinkIcon = new L.Icon({
  iconUrl: markerRed,
  iconSize: [25, 25],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowUrl: 'https://unpkg.com/leaflet@1.7.1/dist/images/marker-shadow.png',
  shadowSize: [41, 41],
});

const greyIcon = new L.Icon({
  iconUrl: markerBlack,
  iconSize: [25, 25],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowUrl: 'https://unpkg.com/leaflet@1.7.1/dist/images/marker-shadow.png',
  shadowSize: [41, 41],
});

// Haversine function to calculate distance (in miles) between two coordinate pairs
const haversineDistance = (coords1, coords2) => {
  const toRad = (x) => (x * Math.PI) / 180;
  const lat1 = coords1.latitude;
  const lon1 = coords1.longitude;
  const lat2 = coords2.latitude;
  const lon2 = coords2.longitude;
  const R = 3958.8; // Earth's radius in miles
  const dLat = toRad(lat2 - lat1);
  const dLon = toRad(lon2 - lon1);
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) ** 2;
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
};

const Distance = ({
  user_id,
  accountStatus,
  selectedVenues,
  setSelectedVenues,
  tagFilteredVenues,
}) => {
  const [currentLocation, setCurrentLocation] = useState(null);
  const [selectedDistance, setSelectedDistance] = useState('');
  const [customLocationInput, setCustomLocationInput] = useState('');
  const [mapHeight, setMapHeight] = useState('700px');
  const [showModal, setShowModal] = useState(false);
  const [modalMessage, setModalMessage] = useState('');

  // Adjust map height on window resize
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 768) {
        setMapHeight('500px');
      } else {
        setMapHeight('700px');
      }
    };
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Obtain user's current location (if permitted)
  useEffect(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition((position) => {
        setCurrentLocation({
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
        });
      });
    }
  }, []);

  const getMaxAllowedVenues = () => {
    if (accountStatus) {
      const plan = accountStatus.subscription.plan;
      if (plan === 'trial' || plan === 'essentials') {
        return 1;
      }
    }
    return Infinity;
  };

  const handleMarkerClick = (venueName) => {
    if (!accountStatus) {
      alert('Loading account status, please wait.');
      return;
    }
    const maxVenues = getMaxAllowedVenues();
    if (selectedVenues.includes(venueName)) {
      setSelectedVenues(selectedVenues.filter((name) => name !== venueName));
      return;
    }
    if (selectedVenues.length >= maxVenues) {
      setModalMessage(
        'Due to your current plan, you can only select one venue. Upgrade to view more.'
      );
      setShowModal(true);
      return;
    }
    setSelectedVenues([...selectedVenues, venueName]);
  };

  // Build a local array of venues to display on the map
  let allVenues = [];
  if (tagFilteredVenues && tagFilteredVenues.length > 0) {
    allVenues = tagFilteredVenues.map((v) => {
      const details = venueDetailsMapping[v.name];
      return {
        name: v.name,
        latitude: details?.latitude,
        longitude: details?.longitude,
        title: details?.title,
        caption: details?.caption,
      };
    });
  }

  // Apply distance filter
  const filteredVenues =
    selectedDistance && currentLocation
      ? allVenues.filter((venue) => {
          if (!venue.latitude || !venue.longitude) return false;
          const distance = haversineDistance(currentLocation, {
            latitude: venue.latitude,
            longitude: venue.longitude,
          });
          return distance <= Number(selectedDistance);
        })
      : allVenues;

  // Determine map center
  const mapCenter = currentLocation
    ? [currentLocation.latitude, currentLocation.longitude]
    : filteredVenues.length > 0
    ? [filteredVenues[0].latitude, filteredVenues[0].longitude]
    : [0, 0];

  // Handle custom location submission
  const handleCustomLocationSubmit = async (e) => {
    e.preventDefault();
    if (!customLocationInput) return;
    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(
          customLocationInput
        )}`
      );
      const data = await response.json();
      if (data && data.length > 0) {
        const { lat, lon } = data[0];
        setCurrentLocation({
          latitude: parseFloat(lat),
          longitude: parseFloat(lon),
        });
      } else {
        alert('Location not found.');
      }
    } catch (err) {
      console.error(err);
      alert('Error fetching location.');
    }
  };

  return (
    <div className="main-distance-page-container">
      <div className="distance-page-container">
        <div className="logo-spacer"></div>

        {/* Page Instructions */}
        <div className="page-instructions">
          <h1>Discover Venues by Distance</h1>
          <p>
            Filter venues matching your tags, optionally limit by distance, then click a marker to select.
          </p>
          <p className="pin-explanation">
            <strong>Red:</strong> previously selected venue &nbsp;|&nbsp; <strong>Black:</strong> other matching venue
          </p>
        </div>

        {/* Distance selector */}
        <div className="distance-controls">
          <label htmlFor="distance-select">
            Select a distance (miles) from your current location:
          </label>
          <select
            id="distance-select"
            value={selectedDistance}
            onChange={(e) => setSelectedDistance(e.target.value)}
          >
            <option value="">All</option>
            <option value="5">5 miles</option>
            <option value="10">10 miles</option>
            <option value="20">20 miles</option>
            <option value="50">50 miles</option>
          </select>
        </div>

        {/* Custom location input */}
        <div className="custom-location-control">
          <form onSubmit={handleCustomLocationSubmit}>
            <label htmlFor="custom-location">Or enter a location:</label>
            <input
              type="text"
              id="custom-location"
              value={customLocationInput}
              onChange={(e) => setCustomLocationInput(e.target.value)}
              placeholder="City, address, etc."
            />
            <button type="submit">Set Location</button>
          </form>
        </div>

        {/* Map and legend */}
        <div className="map-container" style={{ position: 'relative' }}>
          <MapContainer center={mapCenter} zoom={12} style={{ height: mapHeight, width: '100%' }}>
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/">OpenStreetMap</a> contributors'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />
            {filteredVenues.map((venue) => {
              if (!venue.latitude || !venue.longitude) return null;
              const isHighlighted = selectedVenues.includes(venue.name);
              return (
                <Marker
                  key={venue.name}
                  position={[venue.latitude, venue.longitude]}
                  icon={isHighlighted ? pinkIcon : greyIcon}
                  eventHandlers={{ click: () => handleMarkerClick(venue.name) }}
                >
                  <Tooltip direction="top" offset={[0, -20]} opacity={1}>
                    <div className="tooltip-content">
                      <strong>{venue.title}</strong>
                      <p>{venue.caption}</p>
                    </div>
                  </Tooltip>
                </Marker>
              );
            })}
          </MapContainer>

          <div className="map-legend">
            <div className="legend-item">
              <img src={markerRed} alt="Selected Venue" className="legend-icon" /> Selected Venue
            </div>
            <div className="legend-item">
              <img src={markerBlack} alt="Matching Venue" className="legend-icon" /> Matching Venue
            </div>
          </div>
        </div>

        {/* Selected venues bucket */}
        <div className="selected-venues-bucket">
          <h3>Selected Venues</h3>
          {selectedVenues.length === 0 ? (
            <p>No venues selected.</p>
          ) : (
            <div className="selected-venues-list">
              {selectedVenues.map((name) => {
                const details = venueDetailsMapping[name];
                return (
                  <div key={name} className="selected-venue-item">
                    <img src={details.imageUrl} alt={details.title} className="selected-venue-image" />
                    <p>{details.title}</p>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Modal for plan-limit errors */}
      {showModal && (
        <div className="modal-overlay">
          <div className="modal">
            <img src={DangerIcon} alt="Danger Icon" className="danger-icon" />
            <p>{modalMessage}</p>
            <button onClick={() => setShowModal(false)} className="modal-button">
              OK
            </button>
          </div>
        </div>
      )}
    </div>
  );
};


export default Distance;